import { useEffect, useState } from "react";

import { LinkButton } from "@/ui/next/LinkButton";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import { useRouter } from "next/router";

import { SEARCH_ROUTE } from "@/common/static/routes.static";
import { formatAccountMoney } from "@/common/utils/account-format.utils";
import heroImage from "@/features/pro-buyer-application/assets/images/pro-buyer-hero-1-chaise-lamp-4x3.jpg";
import { ProBuyerApplicationBlock } from "@/features/pro-buyer-application/components/ProBuyerApplicationBlock";
import { useProBuyerStatus } from "@/features/pro-buyer-application/hooks/useProBuyerStatus";
import {
  PRO_BUYER_APPLY_ANCHOR,
  PRO_BUYER_EXAMPLE_PCT,
  PRO_BUYER_EXAMPLE_PRICE,
  PRO_BUYER_EXAMPLE_TRADE_PRICE,
  PRO_BUYER_SCROLL_FLAG,
} from "@/features/pro-buyer-application/static/pro-buyer-application.static";
import {
  PRO_BUYER_URL_PARAM,
  PRO_BUYER_URL_VALUE,
} from "@/features/pro-buyer-price/static/pro-buyer-price.static";
import { Button } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { InfoLabel } from "@/ui/cva/InfoLabel";
import { Body } from "@/ui/typography/Body";

/**
 * WHO-5688, design chapter 1: the buyer-side landing page at /pro-buyer.
 * Hero with image, three steps, "Wat je krijgt" with the price example,
 * "Wat we van jou nodig hebben", the application block (#aanvragen) and the
 * FAQ. No bottom CTA. Copy lives in Lokalise (`pro-buyer-application`), not
 * Strapi: the CMS has no landing-page content type (Thomas, 22 Sep).
 */

/** Evelien's hero (22 Sep, chaise and arc lamp, 2000x1500, 4:3), same as step 1. */
export const HERO_IMAGE_SRC = heroImage;

const NS = "pro-buyer-application";

const STEPS = [1, 2, 3].map(index => ({
  titleKey: `${NS}:buyers-step-${index}-title`,
  bodyKey: `${NS}:buyers-step-${index}-body`,
}));

const STEP_DEFAULTS = [
  [
    "Vraag toegang aan",
    "Log in of maak een account en vul je bedrijfsnaam, KvK-nummer en website in. Duurt een minuut.",
  ],
  [
    "Wij kijken mee",
    "We controleren je inschrijving en je website. Je hoort binnen een paar werkdagen per e-mail of je Pro Buyer bent.",
  ],
  [
    "Koop met korting",
    "Zodra je aanvraag is goedgekeurd, zie je direct je Pro Buyer-prijs op items van verkopers die Pro Buyer-korting geven.",
  ],
] as const;

const NEEDS = [
  [
    `${NS}:buyers-need-1`,
    "Een Whoppah-account. Heb je er nog geen, dan maak je die in een minuut",
  ],
  [
    `${NS}:buyers-need-2`,
    "Je bedrijfsnaam en KvK-nummer, of het registratienummer van je land (HRB, SIRET, KBO)",
  ],
  [
    `${NS}:buyers-need-3`,
    "Een website, portfolio of Instagram-account waaraan we zien wat je doet",
  ],
] as const;

const BENEFITS = [
  [
    `${NS}:buyers-benefit-1`,
    "Korting voor Pro Buyers op het aanbod van deelnemende verkopers",
  ],
  [
    `${NS}:buyers-benefit-2`,
    "Je herkent de Pro Buyer-prijs aan het groene label. Alleen zichtbaar als jij bent ingelogd.",
  ],
  [`${NS}:buyers-benefit-3`, "Gratis, zonder abonnement of minimale afname"],
] as const;

const FAQS = [1, 2, 3, 4, 5, 6, 7].map(index => ({
  qKey: `${NS}:buyers-faq-${index}-q`,
  aKey: `${NS}:buyers-faq-${index}-a`,
}));

const FAQ_DEFAULTS = [
  [
    "Wat kost Pro Buyer?",
    "Niets. Pro Buyer is gratis en je zit nergens aan vast.",
  ],
  [
    "Wie kan Pro Buyer worden?",
    "Professionals die interieurs inrichten, zoals interieurontwerpers, architecten, stylisten en stagers, en hospitalitybedrijven (hotels, horeca, kantoren), met een ingeschreven bedrijf.",
  ],
  [
    "Hoe snel hoor ik iets?",
    "Binnen een paar werkdagen. Je krijgt altijd een mail, ook als we meer informatie nodig hebben.",
  ],
  [
    "Wanneer zie ik Pro Buyer-prijzen?",
    "Zodra je aanvraag is goedgekeurd. Je krijgt dan een mail en ziet vanaf dat moment je Pro Buyer-prijs op items van verkopers die Pro Buyer-korting geven.",
  ],
  [
    "Mijn bedrijf staat niet in Nederland ingeschreven. Kan ik toch aanvragen?",
    "Ja. Vul het registratienummer uit je eigen land in, zoals HRB, SIRET of KBO.",
  ],
  [
    "Ziet mijn klant de Pro Buyer-prijs?",
    "Nee. De Pro Buyer-prijs is alleen zichtbaar als jij bent ingelogd. Gedeelde links en zoekresultaten tonen anderen de gewone prijs.",
  ],
  [
    "Ik ben zzp'er zonder btw-nummer. Kan ik aanvragen?",
    "Ja. We vragen geen btw-nummer. Een KvK-inschrijving met een activiteit die bij je vak past is voldoende.",
  ],
] as const;

const FaqItem = ({
  question,
  answer,
  defaultOpen = false,
}: {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-grey-300">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 py-4 text-left font-title text-base font-bold text-black"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        {question}
        <span aria-hidden="true" className="text-brand-500">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <Body as="p" className="pb-4 text-grey-700">
          {answer}
        </Body>
      )}
    </div>
  );
};

const HeroImage = ({ alt }: { alt: string }) => (
  <Image
    src={HERO_IMAGE_SRC}
    alt={alt}
    priority
    placeholder="blur"
    sizes="(min-width: 768px) 464px, 100vw"
    className="aspect-[4/3] w-full rounded-[6px] object-cover"
  />
);

/** WHO-5688 F15: search with the Pro Buyer toggle on, for approved Pro Buyers. */
export const PRO_BUYER_SEARCH_HREF = `${SEARCH_ROUTE}?${PRO_BUYER_URL_PARAM}=${PRO_BUYER_URL_VALUE}`;

const scrollToApplication = () => {
  document
    .getElementById(PRO_BUYER_APPLY_ANCHOR)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const ProBuyerApplicationPage = () => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const money = (value: number) => formatAccountMoney(value, { locale });
  const status = useProBuyerStatus();
  const isProBuyer = status.state === "ready" && status.proBuyer;
  useEffect(() => {
    let pending = false;
    try {
      pending = sessionStorage.getItem(PRO_BUYER_SCROLL_FLAG) === "1";
      sessionStorage.removeItem(PRO_BUYER_SCROLL_FLAG);
    } catch {
      return;
    }

    if (!pending) return;

    // Back from login or signup: let the signed-in block render, then scroll.
    const timer = window.setTimeout(scrollToApplication, 300);
    return () => window.clearTimeout(timer);
  }, []);

  const proBuyerPrice = PRO_BUYER_EXAMPLE_TRADE_PRICE;

  return (
    <div className="flex flex-col" data-testid="pro-buyer-application-page">
      <section className="mx-auto grid w-full max-w-5xl gap-8 px-4 py-12 md:grid-cols-2 md:items-center md:px-8 md:py-16">
        <div className="order-2 flex flex-col items-start gap-4 md:order-1">
          <InfoLabel
            color="brand"
            variant="light"
            size="small"
            corners="square"
          >
            {t(`${NS}:buyers-hero-eyebrow`, "Voor interieurprofessionals")}
          </InfoLabel>
          <Heading as="h1" size="h1">
            {t(
              `${NS}:buyers-hero-title`,
              "Koop design voor je projecten met korting"
            )}
          </Heading>
          <Body as="p" color="muted">
            {t(
              `${NS}:buyers-hero-body`,
              "Met een Pro Buyer-account zie je bij deelnemende verkopers een Pro Buyer-prijs, tot 40% onder de gewone prijs. De verkoper bepaalt zelf of en hoeveel."
            )}
          </Body>
          {isProBuyer ? (
            <LinkButton
              href={PRO_BUYER_SEARCH_HREF}
              appearance="solid"
              color="brand"
              className="w-full text-center md:w-auto"
              data-testid="pro-buyer-hero-member-cta"
            >
              {t(
                `${NS}:buyers-hero-member-cta`,
                "Bekijk items met Pro Buyer-prijs"
              )}
            </LinkButton>
          ) : (
            <Button
              appearance="solid"
              color="brand"
              className="w-full md:w-auto"
              onClick={scrollToApplication}
            >
              {t(`${NS}:buyers-hero-cta`, "Pro Buyer aanvragen")}
            </Button>
          )}
        </div>
        <div className="order-1 md:order-2">
          <HeroImage
            alt={t(
              `${NS}:buyers-hero-alt`,
              "Chaise longue en booglamp in een licht interieur"
            )}
          />
        </div>
      </section>

      <section className="mx-auto w-full max-w-5xl px-4 py-12 md:px-8">
        <Heading as="h2" size="h2">
          {t(`${NS}:buyers-steps-title`, "Zo werkt het")}
        </Heading>
        <ol className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
          {STEPS.map((step, index) => (
            <li key={step.titleKey} className="flex flex-1 flex-col gap-2">
              <span className="flex size-8 items-center justify-center rounded-full bg-brand-100 font-title text-sm font-bold text-brand-500">
                {index + 1}
              </span>
              <Heading as="h3" size="h4">
                {t(step.titleKey, STEP_DEFAULTS[index]?.[0] ?? "")}
              </Heading>
              <Body as="p" color="muted">
                {t(step.bodyKey, STEP_DEFAULTS[index]?.[1] ?? "")}
              </Body>
            </li>
          ))}
        </ol>
      </section>

      <section className="mx-auto grid w-full max-w-5xl gap-12 px-4 py-12 md:grid-cols-2 md:gap-8 md:px-8">
        <div className="flex flex-col items-start gap-4">
          <Heading as="h2" size="h2">
            {t(`${NS}:buyers-benefits-title`, "Wat je krijgt")}
          </Heading>
          <ul className="flex list-disc flex-col gap-2 pl-5 font-body text-base text-black">
            {BENEFITS.map(([key, fallback]) => (
              <li key={key}>{t(key, fallback)}</li>
            ))}
          </ul>
          <div
            className="flex w-full max-w-sm flex-col items-start gap-3 rounded-[6px] bg-grey-50 p-6"
            data-testid="pro-buyer-application-example"
          >
            <InfoLabel
              color="brand"
              variant="light"
              size="small"
              corners="rounded"
            >
              Pro Buyer
            </InfoLabel>
            <dl className="grid w-full grid-cols-[1fr_auto] gap-y-2 font-body text-base text-black">
              <dt>{t(`${NS}:buyers-example-price`, "Vraagprijs")}</dt>
              <dd className="text-right text-grey-700 line-through">
                {money(PRO_BUYER_EXAMPLE_PRICE)}
              </dd>
              <dt className="font-title font-bold">
                {t(`${NS}:buyers-example-pro-price`, "Pro Buyer-prijs")}
              </dt>
              <dd className="text-right font-title font-bold text-brand-500">
                {money(proBuyerPrice)}
              </dd>
            </dl>
            <span className="font-body text-sm text-grey-700">
              {t(`${NS}:buyers-example-note`, {
                defaultValue:
                  "Voorbeeld met {{pct}}% korting. Elke verkoper kiest zelf zijn korting voor Pro Buyers.",
                pct: PRO_BUYER_EXAMPLE_PCT,
              })}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <Heading as="h2" size="h2">
            {t(`${NS}:buyers-needs-title`, "Wat we van jou nodig hebben")}
          </Heading>
          <Body as="p" color="muted">
            {t(
              `${NS}:buyers-needs-intro`,
              "Drie dingen, en je hebt ze waarschijnlijk al bij de hand."
            )}
          </Body>
          <ul className="flex list-disc flex-col gap-2 pl-5 font-body text-base text-black">
            {NEEDS.map(([key, fallback]) => (
              <li key={key}>{t(key, fallback)}</li>
            ))}
          </ul>
          <Body as="p" color="muted">
            {t(
              `${NS}:buyers-needs-closing`,
              "Wij controleren je inschrijving en je website en mailen je binnen een paar werkdagen."
            )}
          </Body>
        </div>
      </section>

      <ProBuyerApplicationBlock />

      <section className="mx-auto w-full max-w-5xl px-4 py-12 md:px-8">
        <Heading as="h2" size="h2">
          {t(`${NS}:buyers-faq-title`, "Veelgestelde vragen")}
        </Heading>
        <div className="mt-4 flex flex-col">
          {FAQS.map((faq, index) => (
            <FaqItem
              key={faq.qKey}
              question={t(faq.qKey, FAQ_DEFAULTS[index]?.[0] ?? "")}
              answer={t(faq.aKey, FAQ_DEFAULTS[index]?.[1] ?? "")}
              defaultOpen={index === 0}
            />
          ))}
        </div>
      </section>
    </div>
  );
};
